import { useNavigation, StackActions } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WebModal } from '@components/Modals/WebModal.web';
import ProjectList from '@components/ProjectList/ProjectList';
import { Box, Text } from '@components/Restyle';
import SearchInput from '@components/shared/SearchInput/SearchInput';
import { useSearchLazyQuery, SearchableResult } from '@graphql/generated';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import { Search, SearchResultType } from '@root/types';
import { isProjectSearchResult } from '@utils/typeGuards';

const createProjectCopySearchEmptyComponent =
  (t: (key: string) => string) => () => (
    <Box mt='xs' marginHorizontal='m'>
      <Text color='grey04' variant='body' textAlign='center'>
        {t('models:projects.copy.noResults')}
      </Text>
    </Box>
  );

export const ProjectCopySearch = () => {
  const navigation = useNavigation();
  const { t } = useTranslation();
  const [searchVal, setSearchVal] = useState('');

  const [globalSearch, { data, loading }] = useSearchLazyQuery();
  const searchData = (data?.search as Search<SearchableResult>[]) || [];
  const initialResults = {
    messages: [],
    tasks: [],
    documents: [],
    projects: [],
  };

  const results =
    searchData.reduce<SearchResultType>((previous, result) => {
      if (isProjectSearchResult(result))
        return { ...previous, projects: [...previous.projects, result] };

      return previous;
    }, initialResults) || initialResults;

  const handleSearch = useCallback(
    (term: string) => {
      globalSearch({
        variables: {
          term,
          size: 20,
          includeMessages: false,
          includeDocuments: false,
          includeProjects: true,
          includeTasks: false,
        },
      });
    },
    [globalSearch]
  );

  const { isPending, isBelowThreshold, hasSearched } = useDebouncedSearch(
    searchVal,
    handleSearch
  );
  // isPending settles to false one render before onSearch actually fires
  // (useDebouncedSearch calls it from an effect) — hasSearched closes that
  // gap so the empty state can't flash before the query is kicked off.
  const isLoading = loading || isPending || (!isBelowThreshold && !hasSearched);

  const projectListData = isBelowThreshold
    ? []
    : results.projects.map((r) => r.record);

  return (
    <WebModal
      visible
      title={t('models:projects.copy.searchModalTitle')}
      onClose={navigation.goBack}
      width={504}
      height={514}>
      <>
        <Box
          flexDirection='row'
          alignItems='center'
          marginTop='m'
          marginHorizontal='m'>
          <SearchInput
            onTextChanged={(newText) => setSearchVal(newText)}
            value={searchVal}
            loading={isLoading}
          />
        </Box>
        {isBelowThreshold ? (
          <Text variant='body' color='grey04' textAlign='center' mt='m'>
            {t('models:projects.copy.searchCta')}
          </Text>
        ) : (
          <ProjectList
            loading={isLoading}
            projects={projectListData}
            expanded={false}
            highlightedValue={searchVal}
            onClick={(item) => {
              navigation.dispatch(
                StackActions.replace('project-copy-select', {
                  projectId: item.id,
                  projectUUId: item.uuid,
                })
              );
            }}
            listEmptyComponent={createProjectCopySearchEmptyComponent(t)}
          />
        )}
      </>
    </WebModal>
  );
};
